import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BASE_URL } from '../config/socket';

function StockHistory() {
  const [activeTab, setActiveTab] = useState('low-stock'); // 'low-stock', 'manage', 'history'
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [quickFilter, setQuickFilter] = useState('all'); // 'all', 'today', 'week', 'in', 'out'

  const fetchMovements = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/stock-movements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMovements(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stock movements:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMovements();
    fetchProducts();
  }, [fetchMovements]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleStockChange = async (product, type) => {
    const { value: quantity } = await Swal.fire({
      title: `${type === 'in' ? '📥 เพิ่ม' : type === 'out' ? '📤 ลด' : '🔧 ปรับ'}สต็อก`,
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="font-size: 16px; margin-bottom: 10px;"><strong>${product.name}</strong></p>
          <p style="color: #666;">สต็อกปัจจุบัน: <strong style="font-size: 20px; color: #0ea5e9;">${product.stock_quantity}</strong></p>
        </div>
      `,
      input: 'number',
      inputLabel: 'จำนวน',
      inputPlaceholder: 'ระบุจำนวน...',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: type === 'in' ? '#10b981' : type === 'out' ? '#ef4444' : '#0ea5e9',
      inputValidator: (value) => {
        if (!value || value <= 0) {
          return 'กรุณาระบุจำนวนที่มากกว่า 0';
        }
      }
    });

    if (quantity) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${BASE_URL}/stock-movements`,
          {
            product_id: product.id,
            movement_type: type,
            quantity: parseInt(quantity),
            notes: `${type === 'in' ? 'เพิ่ม' : type === 'out' ? 'ลด' : 'ปรับ'}สต็อกด้วยตนเอง`
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        await Swal.fire({
          icon: 'success',
          title: 'สำเร็จ!',
          text: `${type === 'in' ? 'เพิ่ม' : type === 'out' ? 'ลด' : 'ปรับ'}สต็อกเรียบร้อยแล้ว`,
          timer: 1500,
          showConfirmButton: false
        });

        fetchMovements();
        fetchProducts();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: error.response?.data?.error || 'ไม่สามารถปรับสต็อกได้'
        });
      }
    }
  };

  // Filter products for low stock
  const lowStockProducts = products.filter(p => p.stock_quantity <= p.min_stock_level);

  // Filter products for manage tab
  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter movements for history tab
  const getFilteredMovements = () => {
    let filtered = movements;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    switch (quickFilter) {
      case 'today':
        filtered = movements.filter(m => new Date(m.movement_date) >= today);
        break;
      case 'week':
        filtered = movements.filter(m => new Date(m.movement_date) >= weekAgo);
        break;
      case 'in':
        filtered = movements.filter(m => m.movement_type === 'in');
        break;
      case 'out':
        filtered = movements.filter(m => m.movement_type === 'out');
        break;
      default:
        break;
    }

    if (searchTerm) {
      filtered = filtered.filter(m =>
        m.product_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const exportCSV = () => {
    const data = getFilteredMovements();
    if (data.length === 0) {
      Swal.fire({ icon: 'warning', title: 'ไม่มีข้อมูล', timer: 1500 });
      return;
    }

    const csv = [
      ['วันที่', 'ประเภท', 'สินค้า', 'จำนวน', 'หมายเหตุ'],
      ...data.map(m => [
        new Date(m.movement_date).toLocaleString('th-TH'),
        m.movement_type === 'in' ? 'นำเข้า' : m.movement_type === 'out' ? 'ขาย' : 'ปรับปรุง',
        m.product_name,
        m.quantity,
        m.notes || '-'
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `stock_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    Swal.fire({ icon: 'success', title: 'ส่งออกสำเร็จ', timer: 1500, showConfirmButton: false });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-2xl">⏳ กำลังโหลด...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">📦 จัดการสต็อกสินค้า</h1>
        <p className="text-gray-600">จัดการสต็อก ดูประวัติ และแจ้งเตือนสต็อกต่ำ</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
          <div className="text-3xl mb-2">📦</div>
          <div className="text-2xl font-bold">{products.length}</div>
          <div className="text-sm opacity-90">สินค้าทั้งหมด</div>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow" onClick={() => setActiveTab('low-stock')}>
          <div className="text-3xl mb-2">🚨</div>
          <div className="text-2xl font-bold">{lowStockProducts.length}</div>
          <div className="text-sm opacity-90">สต็อกต่ำ (คลิกดู)</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white shadow-lg">
          <div className="text-3xl mb-2">📥</div>
          <div className="text-2xl font-bold">
            {movements.filter(m => m.movement_type === 'in').reduce((sum, m) => sum + m.quantity, 0)}
          </div>
          <div className="text-sm opacity-90">รวมนำเข้า</div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white shadow-lg">
          <div className="text-3xl mb-2">📤</div>
          <div className="text-2xl font-bold">
            {movements.filter(m => m.movement_type === 'out').reduce((sum, m) => sum + m.quantity, 0)}
          </div>
          <div className="text-sm opacity-90">รวมขาย</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('low-stock')}
            className={`flex-1 px-6 py-4 font-semibold text-lg transition-all ${
              activeTab === 'low-stock'
                ? 'bg-red-50 text-red-600 border-b-4 border-red-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            🚨 สต็อกต่ำ {lowStockProducts.length > 0 && `(${lowStockProducts.length})`}
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`flex-1 px-6 py-4 font-semibold text-lg transition-all ${
              activeTab === 'manage'
                ? 'bg-blue-50 text-blue-600 border-b-4 border-blue-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            🔧 จัดการสต็อก
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 px-6 py-4 font-semibold text-lg transition-all ${
              activeTab === 'history'
                ? 'bg-green-50 text-green-600 border-b-4 border-green-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            📊 ประวัติ
          </button>
        </div>
      </div>

      {/* Low Stock Tab */}
      {activeTab === 'low-stock' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-red-600">⚠️ สินค้าที่สต็อกต่ำกว่าขั้นต่ำ</h2>
            {lowStockProducts.length > 0 && (
              <span className="bg-red-100 text-red-800 px-4 py-2 rounded-full font-bold">
                {lowStockProducts.length} รายการ
              </span>
            )}
          </div>

          {lowStockProducts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">✅</div>
              <div className="text-xl text-gray-600">ไม่มีสินค้าที่สต็อกต่ำ</div>
              <div className="text-gray-500 mt-2">สต็อกสินค้าทั้งหมดอยู่ในระดับปกติ</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-bold text-red-900">สินค้า</th>
                    <th className="px-6 py-3 text-center text-sm font-bold text-red-900">สต็อกปัจจุบัน</th>
                    <th className="px-6 py-3 text-center text-sm font-bold text-red-900">ขั้นต่ำ</th>
                    <th className="px-6 py-3 text-center text-sm font-bold text-red-900">สถานะ</th>
                    <th className="px-6 py-3 text-center text-sm font-bold text-red-900">จัดการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {lowStockProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-red-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{product.name}</div>
                        {product.barcode && (
                          <div className="text-sm text-gray-500">{product.barcode}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-2xl font-bold text-red-600">
                          {product.stock_quantity}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-lg text-gray-600">
                          {product.min_stock_level}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {product.stock_quantity === 0 ? (
                          <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-bold">
                            ⛔ หมด
                          </span>
                        ) : (
                          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-bold">
                            ⚠️ ต่ำ
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleStockChange(product, 'in')}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                          >
                            📥 เพิ่ม
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Manage Stock Tab */}
      {activeTab === 'manage' && (
        <div>
          {/* Search Bar */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="🔍 ค้นหาสินค้า (ชื่อ หรือ บาร์โค้ด)... สามารถใช้ Barcode Scanner ได้"
              className="w-full p-4 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
            {searchTerm && (
              <div className="mt-2 text-sm text-gray-600">
                พบ <strong className="text-blue-600">{filteredProducts.length}</strong> รายการ
              </div>
            )}
          </div>

          {/* Products Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold">สินค้า</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">สต็อก</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">ขั้นต่ำ</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">จัดการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                        <div className="text-6xl mb-4">🔍</div>
                        <div className="text-xl">ไม่พบสินค้า</div>
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-blue-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-semibold text-gray-900">{product.name}</div>
                          {product.barcode && (
                            <div className="text-sm text-gray-500">{product.barcode}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`text-2xl font-bold ${
                            product.stock_quantity <= product.min_stock_level
                              ? 'text-red-600'
                              : product.stock_quantity <= product.min_stock_level * 2
                              ? 'text-yellow-600'
                              : 'text-green-600'
                          }`}>
                            {product.stock_quantity}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center text-gray-600">
                          {product.min_stock_level}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleStockChange(product, 'in')}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg font-semibold transition-colors text-sm"
                              title="เพิ่มสต็อก"
                            >
                              📥 เพิ่ม
                            </button>
                            <button
                              onClick={() => handleStockChange(product, 'out')}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg font-semibold transition-colors text-sm"
                              title="ลดสต็อก"
                            >
                              📤 ลด
                            </button>
                            <button
                              onClick={() => handleStockChange(product, 'adjustment')}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-semibold transition-colors text-sm"
                              title="ปรับสต็อก"
                            >
                              🔧 ปรับ
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          {/* Quick Filters & Search */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex flex-wrap gap-3 mb-4">
              <button
                onClick={() => setQuickFilter('all')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  quickFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📋 ทั้งหมด
              </button>
              <button
                onClick={() => setQuickFilter('today')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  quickFilter === 'today'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📅 วันนี้
              </button>
              <button
                onClick={() => setQuickFilter('week')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  quickFilter === 'week'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📆 7 วัน
              </button>
              <button
                onClick={() => setQuickFilter('in')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  quickFilter === 'in'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📥 นำเข้า
              </button>
              <button
                onClick={() => setQuickFilter('out')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  quickFilter === 'out'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📤 ขาย
              </button>
              <button
                onClick={exportCSV}
                className="ml-auto bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                💾 Export CSV
              </button>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="🔍 ค้นหาสินค้า..."
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* History Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold">วันที่-เวลา</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">ประเภท</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">สินค้า</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">จำนวน</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">หมายเหตุ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {getFilteredMovements().length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        <div className="text-6xl mb-4">📭</div>
                        <div className="text-xl">ไม่มีประวัติ</div>
                      </td>
                    </tr>
                  ) : (
                    getFilteredMovements().slice(0, 50).map((movement) => (
                      <tr key={movement.id} className="hover:bg-green-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {new Date(movement.movement_date).toLocaleString('th-TH')}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            movement.movement_type === 'in'
                              ? 'bg-green-100 text-green-800'
                              : movement.movement_type === 'out'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {movement.movement_type === 'in' ? '📥 นำเข้า' : movement.movement_type === 'out' ? '📤 ขาย' : '🔧 ปรับ'}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-semibold text-gray-900">
                          {movement.product_name}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`text-lg font-bold ${
                            movement.movement_type === 'in' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {movement.movement_type === 'in' ? '+' : '-'}{movement.quantity}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {movement.notes || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {getFilteredMovements().length > 50 && (
              <div className="bg-gray-50 px-6 py-4 text-center text-sm text-gray-600">
                แสดง 50 รายการแรก จากทั้งหมด {getFilteredMovements().length} รายการ
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StockHistory;
